// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunnerOptionalParameter,
    HasOnError,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { updateAccount, UpdateAccountApiParameter } from "@insite/client-framework/Services/AccountService";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import setInitialValues from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/SetInitialValues";
import { AccountModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = ApiHandlerDiscreteParameter<
    HasOnSuccess & HasOnError<string>,
    UpdateAccountApiParameter,
    AccountModel,
    { account: AccountModel }
>;

export const GetCurrentAccount: HandlerType = props => {
    const account = getCurrentAccountState(props.getState()).value;
    props.account = account || ({} as AccountModel);
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        account: {
            ...props.account,
            properties: {
                ...props.account.properties,
                plumbers_setSeenJobListsAnnouncement: "true",
            },
        },
    };
};

export const CallUpdateAccount: HandlerType = async props => {
    try {
        props.apiResult = await updateAccount(props.apiParameter);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }
        throw error;
    }
};

export const UpdateSession: HandlerType = props => {
    props.dispatch({
        type: "Context/CompleteLoadSession",
        session: {
            ...props.getState().context.session,
            email: props.apiResult.email,
            userName: props.apiResult.userName,
            properties: {
                ...props.getState().context.session.properties,
                pls_seenJobListsAnnouncement: "true",
            },
        },
    });
};

export const LoadCurrentAccount: HandlerType = props => {
    props.dispatch(loadCurrentAccount());
};

export const SetInitialValues: HandlerType = props => {
    props.dispatch(setInitialValues());
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.();
};

export const chain = [
    GetCurrentAccount,
    PopulateApiParameter,
    CallUpdateAccount,
    UpdateSession,
    LoadCurrentAccount,
    SetInitialValues,
    ExecuteOnSuccessCallback,
];

const closeJobListsInfoPopup = createHandlerChainRunnerOptionalParameter(chain, {}, "CloseJobListsInfoPopup");
export default closeJobListsInfoPopup;
